// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import CardScanPanel from "./CardScanPanel";
import { useToast } from "./ToastProvider";
import { getCollectionItems } from "../api/cards";
import { getUser } from "../auth/auth";
import { preparePostTradeDraftFromCard } from "../utils/postTradeDraftPrefill";
import "../styles/NavbarScannerModal.css";

interface NavbarScannerModalProps {
  launchMode: "camera" | "upload";
  onRequestClose: () => void;
}

export default function NavbarScannerModal({
  launchMode,
  onRequestClose,
}: NavbarScannerModalProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const user = getUser();
  const [items, setItems] = useState({ readyToSave: [], saved: [] });
  const [cameraVisible, setCameraVisible] = useState(false);
  const [scannerActivated, setScannerActivated] = useState(launchMode !== "camera");
  const cameraOpenedOnceRef = useRef(false);
  const hideChrome = launchMode === "camera" && !scannerActivated;

  const loadCollection = useCallback(async () => {
    try {
      const data = await getCollectionItems();
      setItems({
        readyToSave: Array.isArray(data?.readyToSave) ? data.readyToSave : [],
        saved: Array.isArray(data?.saved) ? data.saved : [],
      });
    } catch (error) {
      console.error("Failed to load collection items for navbar scanner.", error);
      setItems({ readyToSave: [], saved: [] });
    }
  }, []);

  useEffect(() => {
    void loadCollection();
  }, [loadCollection]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onRequestClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onRequestClose]);

  useEffect(() => {
    if (launchMode !== "camera") return;
    if (cameraVisible) {
      cameraOpenedOnceRef.current = true;
      return;
    }
    if (cameraOpenedOnceRef.current && !scannerActivated) {
      onRequestClose();
    }
  }, [cameraVisible, launchMode, onRequestClose, scannerActivated]);

  const handlePostTrade = useCallback(
    async ({ confirmedCard, frontFile, condition, gradingCompany, gradeNumber }) => {
      if (!confirmedCard?.cardId || !confirmedCard?.game) {
        showToast("Confirm a scanned card before posting a trade.", "warning");
        return;
      }

      const draftResult = await preparePostTradeDraftFromCard({
        userId: user?.id ?? null,
        sourceContext: "scanner",
        confirmedCard,
        cardName: confirmedCard.cardName,
        condition,
        gradingCompany,
        gradeNumber,
        frontFile: frontFile || null,
        imageFileName: frontFile?.name || "trade-image.webp",
        blockOnImageFailure: false,
      });

      if (!draftResult.ok) {
        showToast(draftResult.error || "Failed to prepare the trade draft.", "error");
        return;
      }
      if (draftResult.warning) {
        showToast(draftResult.warning, "warning");
      }

      onRequestClose();
      navigate("/post-trade");
    },
    [navigate, onRequestClose, showToast, user?.id],
  );

  return createPortal(
    <div className="navbar-scanner-modal-root" role="dialog" aria-modal="true" aria-labelledby="navbar-scanner-title">
      <div
        className={`navbar-scanner-modal-backdrop ${hideChrome ? "is-hidden" : ""}`}
        onClick={onRequestClose}
      />
      <div className={`navbar-scanner-modal-shell ${hideChrome ? "is-hidden" : ""}`}>
        <div className="navbar-scanner-modal-content">
          <div className="navbar-scanner-modal-header">
            <div>
              <h2 id="navbar-scanner-title">Card Scanner</h2>
              <p>Scan cards from anywhere, keep them in Ready to Save, or jump straight into a trade draft.</p>
            </div>
            <button type="button" className="btn-secondary navbar-scanner-modal-close" onClick={onRequestClose}>
              Close
            </button>
          </div>

          <CardScanPanel
            flowContext="collection"
            variant="navbarModal"
            launchMode={launchMode}
            onRequestClose={onRequestClose}
            onCameraVisibilityChange={setCameraVisible}
            onFilesChange={({ frontFile, backFile }) => {
              if (frontFile || backFile) {
                setScannerActivated(true);
              }
            }}
            title="Collection Scanner"
            subtitle="Upload a front card image or capture one or more cards, confirm the catalog match, then add them to collection or use the active card for a trade."
            readyToSaveItems={items.readyToSave}
            savedCollectionItems={items.saved}
            onCollectionChanged={loadCollection}
            onConfirmed={async () => {
              await loadCollection();
            }}
            onPostTrade={handlePostTrade}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
